"use client";

import Image from "next/image";
import Link from "next/link";
import { ArrowRightIcon } from "lucide-react";

import { Button } from "@/components/ui/button";
import { landingNavLinks } from "./landing-content";

const NAV_HEIGHT = 64;

const accountLinks = [
  { label: "Create an account", href: "/register" },
  { label: "Log in", href: "/login" },
  { label: "Reset your password", href: "/reset-password" },
];

const legalLinks = [
  { label: "Privacy policy", href: "/privacy" },
  { label: "Terms of service", href: "/terms" },
  { label: "Data processing agreement", href: "/terms/dpa" },
];

export function FooterSection({ showCta = true }: { showCta?: boolean }) {
  function scrollToSection(id: string) {
    const el = document.getElementById(id);
    // The footer also closes the legal pages, where these sections don't
    // exist; send the visitor to them on the landing page instead of
    // leaving a button that does nothing.
    if (!el) {
      window.location.assign(`/#${id}`);
      return;
    }
    window.scrollTo({
      top: el.getBoundingClientRect().top + window.scrollY - NAV_HEIGHT,
      behavior: "smooth",
    });
  }

  return (
    <>
      {/* The page's one accent surface, spent on the strongest conversion
          point. Dark mode's brand is a bright cyan, so a full petrol fill
          glares there — it becomes a deep panel lit by a petrol glow instead. */}
      {showCta ? (
      <section className="px-4 py-20 sm:px-6 sm:py-24">
        <div className="bg-brand text-brand-foreground dark:bg-card dark:text-foreground relative isolate mx-auto max-w-5xl overflow-hidden rounded-3xl px-6 py-16 text-center sm:px-10 sm:py-20 dark:border">
          <div
            aria-hidden="true"
            className="bg-brand/25 absolute -top-24 left-1/2 -z-10 hidden size-72 -translate-x-1/2 rounded-full blur-3xl dark:block"
          />
          <h2 className="text-3xl font-semibold tracking-tight text-balance sm:text-4xl">
            Ready to fill your calendar?
          </h2>
          <p className="dark:text-muted-foreground mx-auto mt-4 max-w-xl text-base text-pretty opacity-90 sm:text-lg dark:opacity-100">
            Set up your services and hours, share your booking page, and let
            customers book themselves in.
          </p>
          <Button
            render={<Link href="/register" />}
            nativeButton={false}
            className="bg-background text-foreground hover:bg-background/90 dark:bg-brand dark:text-brand-foreground dark:hover:bg-brand/90 mt-8 h-12 rounded-full px-8 text-base"
          >
            Start for free
            <ArrowRightIcon data-icon="inline-end" />
          </Button>
          <p className="dark:text-muted-foreground mt-4 text-sm opacity-80 dark:opacity-100">
            No card needed
          </p>
        </div>
      </section>
      ) : null}

      <footer className="border-t px-4 py-14 sm:px-6">
        <div className="mx-auto grid max-w-6xl gap-10 sm:grid-cols-2 lg:grid-cols-[2fr_1fr_1fr_1fr]">
          <div>
            <div className="flex items-center gap-2">
              <Image
                src="/landing/manito-logo-dark.svg"
                alt=""
                width={32}
                height={28}
                className="h-7 w-auto dark:hidden"
              />
              <Image
                src="/landing/manito-logo.svg"
                alt=""
                width={32}
                height={28}
                className="hidden h-7 w-auto dark:block"
              />
              <span className="text-lg font-semibold tracking-tight">
                Manito
              </span>
            </div>
            <p className="text-muted-foreground mt-4 max-w-xs text-sm leading-relaxed">
              Online booking, a shared team calendar and reminders for
              businesses that run on appointments.
            </p>
          </div>

          <nav aria-label="Product">
            <h3 className="text-sm font-semibold">Product</h3>
            <ul className="mt-4 space-y-3">
              {landingNavLinks.map((link) => (
                <li key={link.id}>
                  <button
                    type="button"
                    onClick={() => scrollToSection(link.id)}
                    className="text-muted-foreground hover:text-foreground text-sm transition-colors"
                  >
                    {link.label}
                  </button>
                </li>
              ))}
            </ul>
          </nav>

          <nav aria-label="Account">
            <h3 className="text-sm font-semibold">Account</h3>
            <ul className="mt-4 space-y-3">
              {accountLinks.map((link) => (
                <li key={link.href}>
                  <Link
                    href={link.href}
                    className="text-muted-foreground hover:text-foreground text-sm transition-colors"
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </nav>

          <nav aria-label="Legal">
            <h3 className="text-sm font-semibold">Legal</h3>
            <ul className="mt-4 space-y-3">
              {legalLinks.map((link) => (
                <li key={link.href}>
                  <Link
                    href={link.href}
                    className="text-muted-foreground hover:text-foreground text-sm transition-colors"
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </nav>
        </div>

        <div className="text-muted-foreground mx-auto mt-12 flex max-w-6xl flex-col items-center justify-between gap-2 border-t pt-6 text-sm sm:flex-row">
          <p>© {new Date().getFullYear()} Manito Appointments</p>
          <p>Made in Cyprus</p>
        </div>
      </footer>
    </>
  );
}
